import { useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useCampaignProjectLabels } from 'modules/campaigns/editor/hooks';
import { addPositiveToast } from 'modules/toastContainer/utils';
import {
  getWorkflow,
  getIsJourneyLabelsEnabled,
  getIsJourneyExitEventsEnabled,
} from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { useUpdateTileMetrics, useUpdateWorkflowDetails } from 'modules/workflows/hooks';
import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsJourneyGoalsWithExitsFeatureFlagOn,
  selectIsJourneySettingsRefactorFeatureFlagOn,
} from 'core/userContext/selectors';
import { useAddJourneyConversions } from 'modules/workflows/hooks/useAddJourneyConversion';
import { AttributionMode } from 'modules/domains/campaign';
import { useUpdateExitRules } from 'modules/workflows/hooks/useUpdateExitRules';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { useJourneyVisitorTracking } from 'modules/workflows/hooks/useJourneyVisitorTracking';
import { SettingsTab, LABELS_FIELD } from '../SettingsTakeover.constants';
import { getSettingsTabs, getJourneyLabels } from '../SettingsTakeover.utils';

type JourneySettingsProps = {
  onSuccess?: () => void;
  onSetTabValue?: (tabValue: SettingsTab) => void;
};

type JourneySettings = {
  isSaving: boolean;
  saveJourneySettings: (finalFn?: () => void) => void;
};

export interface SaveJourneySettingsResponse {
  hasErrors?: boolean;
}

const SUCCESS_MESSAGE = 'Success! Journey settings updated.';

export const useSaveJourneySettingsOld = ({
  onSuccess,
  onSetTabValue,
}: JourneySettingsProps): JourneySettings => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);

  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyLabelsEnabled = useSelector(getIsJourneyLabelsEnabled);
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  const { id: workflowId, workflowType } = workflow;
  const publishedWorkflowId = workflow?.workflowReference?.id;

  const { getExitRules } = useExitRuleContext();
  const updateWorkflowDetails = useUpdateWorkflowDetails();
  const updateTileMetrics = useUpdateTileMetrics();
  const addJourneyConversions = useAddJourneyConversions();
  const updateExitRules = useUpdateExitRules();
  const setJourneyVisitorTracking = useJourneyVisitorTracking();
  const { getValues } = useFormContext();
  const { dirtyFields } = useFormState();
  const { results: projectLabels } = useCampaignProjectLabels();

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = (res: SaveJourneySettingsResponse[] = []) => {
    const tabHasErrorIdx = res.findIndex((item) => item?.hasErrors);
    if (tabHasErrorIdx >= 0)
      return onSetTabValue?.(getSettingsTabs(isJourneyGoalsEnabled)[tabHasErrorIdx]);
    if (onSuccess) onSuccess();
    dispatch(addPositiveToast(SUCCESS_MESSAGE));
  };

  const saveJourneySettings = () => {
    setIsSaving(true);
    const {
      about: aboutTabFields,
      metrics: metricsTabFields,
      conversions,
      exitRules,
    } = getValues();

    const workflowDetails = {
      workflowId,
      workflowType,
      ...aboutTabFields,
      [LABELS_FIELD]: isJourneyLabelsEnabled
        ? getJourneyLabels(aboutTabFields[LABELS_FIELD], projectLabels)
        : undefined,
    };

    const sendMetrics = {
      journeyId: workflowId,
      journeyType: workflowType as WorkflowType,
      metricNamesInput: {
        ...metricsTabFields,
      },
    };

    const conversionFields = {
      eventName: conversions?.event,
      attributionMode:
        conversions?.mode === AttributionMode.LastMessaged
          ? AttributionMode.AttributionPeriod
          : conversions?.mode,
      attributionPeriodHours: Number(conversions?.periodHours),
    };

    const shouldUpdateWorkflowDetails = () => {
      const { name, description, labels } = dirtyFields?.about || {};
      return name || description || labels;
    };

    // order should match SETTINGS_TABS
    const promises = [
      // only update changed fields
      shouldUpdateWorkflowDetails()
        ? updateWorkflowDetails({
            workflowDetails,
            showSuccessToast: false,
          })
        : {},

      dirtyFields.metrics
        ? updateTileMetrics({
            sendMetrics,
            showSuccessToast: false,
          })
        : {},

      isJourneyGoalsEnabled && !!Object.keys(dirtyFields?.conversions || {})?.length
        ? addJourneyConversions({
            conversions: [conversionFields],
            showSuccessToast: false,
          })
        : {},

      isJourneyGoalsWithExitsFeatureFlagOn && dirtyFields.exitRules
        ? updateExitRules({ exitRules })
        : {},

      isJourneyExitEventsEnabled && dirtyFields?.about?.visitorTrackingEnabled
        ? setJourneyVisitorTracking(
            workflowType === WorkflowType.DRAFT ? publishedWorkflowId : workflowId,
            aboutTabFields.visitorTrackingEnabled,
          )
        : {},
    ];

    Promise.all(promises)
      .then(handleSaveSettings)
      .catch(handleSaveSettings)
      .finally(() => {
        getExitRules();
        setIsSaving(false);
      });
  };

  return {
    isSaving,
    saveJourneySettings,
  };
};

export const useSaveJourneySettingsNew = ({
  onSuccess,
  onSetTabValue,
}: JourneySettingsProps): JourneySettings => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);

  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyLabelsEnabled = useSelector(getIsJourneyLabelsEnabled);
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  const { id: workflowId, workflowType } = workflow;
  const publishedWorkflowId = workflow?.workflowReference?.id;

  const { getExitRules } = useExitRuleContext();
  const updateWorkflowDetails = useUpdateWorkflowDetails();
  const updateTileMetrics = useUpdateTileMetrics();
  const addJourneyConversions = useAddJourneyConversions();
  const updateExitRules = useUpdateExitRules();
  const setJourneyVisitorTracking = useJourneyVisitorTracking();
  const { getValues } = useFormContext();
  const { dirtyFields } = useFormState();
  const { results: projectLabels } = useCampaignProjectLabels();

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveSettings = (res: SaveJourneySettingsResponse[] = []) => {
    const tabHasErrorIdx = res.findIndex((item) => item?.hasErrors);
    if (onSetTabValue && tabHasErrorIdx >= 0)
      return onSetTabValue?.(getSettingsTabs(isJourneyGoalsEnabled)[tabHasErrorIdx]);
    if (onSuccess) onSuccess();
    dispatch(addPositiveToast(SUCCESS_MESSAGE));
  };

  const saveJourneySettings = (finishSaving?: () => void) => {
    setIsSaving(true);
    const {
      about: aboutTabFields,
      metrics: metricsTabFields,
      conversions,
      'exit-rules': exitRules,
    } = getValues();

    const workflowDetails = {
      workflowId,
      workflowType,
      ...aboutTabFields,
      [LABELS_FIELD]: isJourneyLabelsEnabled
        ? getJourneyLabels(aboutTabFields[LABELS_FIELD], projectLabels)
        : undefined,
    };

    const sendMetrics = {
      journeyId: workflowId,
      journeyType: workflowType as WorkflowType,
      metricNamesInput: {
        ...metricsTabFields,
      },
    };

    const conversionFields = {
      eventName: conversions?.event,
      attributionMode:
        conversions?.mode === AttributionMode.LastMessaged
          ? AttributionMode.AttributionPeriod
          : conversions?.mode,
      attributionPeriodHours: Number(conversions?.periodHours),
    };

    const shouldUpdateWorkflowDetails = () => {
      const { name, description, labels } = dirtyFields?.about || {};
      return name || description || labels;
    };

    // order should match SETTINGS_TABS
    const promises = [
      // only update changed fields
      shouldUpdateWorkflowDetails()
        ? updateWorkflowDetails({
            workflowDetails,
            showSuccessToast: false,
          })
        : {},

      dirtyFields.metrics
        ? updateTileMetrics({
            sendMetrics,
            showSuccessToast: false,
          })
        : {},

      isJourneyGoalsEnabled && !!Object.keys(dirtyFields?.conversions || {})?.length
        ? addJourneyConversions({
            conversions: [conversionFields],
            showSuccessToast: false,
          })
        : {},

      isJourneyGoalsWithExitsFeatureFlagOn && dirtyFields['exit-rules']
        ? updateExitRules({ exitRules })
        : {},

      isJourneyExitEventsEnabled && dirtyFields?.about?.visitorTrackingEnabled
        ? setJourneyVisitorTracking(
            workflowType === WorkflowType.DRAFT ? publishedWorkflowId : workflowId,
            aboutTabFields.visitorTrackingEnabled,
          )
        : {},
    ];

    Promise.all(promises)
      .then(handleSaveSettings)
      .catch(handleSaveSettings)
      .finally(async () => {
        await getExitRules();
        setIsSaving(false);
      })
      .finally(() => {
        finishSaving?.();
      });
  };

  return {
    isSaving,
    saveJourneySettings,
  };
};

export const useSaveJourneySettings = (props: JourneySettingsProps) => {
  const isJourneySettingsFeatureFlagEnabled = useSelector(
    selectIsJourneySettingsRefactorFeatureFlagOn,
  );
  return isJourneySettingsFeatureFlagEnabled
    ? useSaveJourneySettingsNew(props)
    : useSaveJourneySettingsOld(props);
};
